import type { PostgresLegacyAccessManagementConfig } from "@p0security/integrations-postgres-legacy-shared/types";
import { PostgresLegacyServiceAccountExtension } from "@p0security/integrations-postgres-legacy-shared/utils";
import type { Instructions } from "@p0security/shared/install/types";
import { Spacing, Typography } from "@p0security/ui";

import { buildSql } from "./sqlBuilder";

export const gcpCloudSqlInstructor = (
  item: Readonly<PostgresLegacyAccessManagementConfig>,
  serviceAccountEmail: string
): Instructions<any> => {
  // Return empty for RDS instructions.
  if (item.installType.type !== "cloud-sql") {
    return {};
  }
  const formattedServiceAccountEmail = serviceAccountEmail.split(
    PostgresLegacyServiceAccountExtension.CLOUD_SQL
  )[0];

  const { installType } = item;
  const { projectId, instanceId } = installType;
  const sql = buildSql(formattedServiceAccountEmail, "cloud-sql");

  return {
    help: (
      <Typography.Paragraph>
        Follow the steps below to configure your GCP CloudSQL instance and
        Postgres database to allow P0 to manage access. The instance must have
        IAM database authentication enabled. Set the
        <code>cloudsql.iam_authentication</code> database flag to{" "}
        <code>on</code>. All of these commands only need to be run once per
        CloudSQL instance; if you are configuring multiple databases on the same
        instance and have already run these commands on this instance, you can
        click &quot;Next&quot; to continue.
      </Typography.Paragraph>
    ),
    commands: {
      shell: [
        {
          header: (
            <>
              <Typography.Paragraph>
                <strong>Step 1: </strong>Run the following commands to authorize
                the P0 service account and provide the necessary permissions to
                establish a connection with the Cloud SQL instance.
              </Typography.Paragraph>
              <Typography.Paragraph>
                The commands create a custom role that allows P0 to create and
                delete users in your Cloud SQL instance, assign the permissions
                that P0 needs to access your Cloud SQL instance, and add the
                service account as a Cloud SQL instance user.
              </Typography.Paragraph>
              <Typography.Paragraph>
                If you don&apos;t have the
                <span className="ant-typography">
                  <code>gcloud</code>
                </span>
                CLI configured, you can also use
                <Spacing asChild ml="5px">
                  <a
                    className="ant-typography"
                    href="https://shell.cloud.google.com"
                    rel="noreferrer"
                    target="_blank"
                  >
                    Google Cloud Shell.
                  </a>
                </Spacing>
              </Typography.Paragraph>
            </>
          ),
          command: `
gcloud iam roles create p0CloudSQLUserManager \\
--project=${projectId} \\
--title="P0 Cloud SQL User Manager" \\
--description="Allows P0 to create and delete users in your Cloud SQL instance" \\
--permissions=cloudsql.users.create,cloudsql.users.delete  
  
gcloud projects add-iam-policy-binding ${projectId} \\
--member="serviceAccount:${serviceAccountEmail}" \\
--role="roles/cloudsql.client" --condition=None

gcloud projects add-iam-policy-binding ${projectId} \\
--member="serviceAccount:${serviceAccountEmail}" \\
--role="roles/cloudsql.instanceUser" --condition=None 

gcloud projects add-iam-policy-binding ${projectId} \\
--member="serviceAccount:${serviceAccountEmail}" \\
--role="roles/cloudsql.viewer" --condition=None 

gcloud projects add-iam-policy-binding ${projectId} \\
--member="serviceAccount:${serviceAccountEmail}" \\
--role="projects/${projectId}/roles/p0CloudSQLUserManager" --condition=None  

gcloud sql users create ${formattedServiceAccountEmail} \\
--project=${projectId} \\
--instance="${instanceId}" \\
--type=cloud_iam_service_account

`,
        },
        // The next two items can probably be moved verbatim to a set of common instructions,
        // whenever we get another Postgres integration type (native, AWS RDS, etc.)
        {
          header: (
            <>
              <Typography.Paragraph>
                <strong>Step 2:</strong> Run the following SQL in your Cloud SQL
                instance to create the required functions.
              </Typography.Paragraph>
              <ul>
                <li>
                  <Typography.Paragraph>
                    Ensure you connect to the Cloud SQL instance as an
                    authorized user with these required permissions:
                  </Typography.Paragraph>
                </li>
                <ul>
                  <li>Permission to grant access (GRANT) to other users.</li>
                  <li>
                    Permission to create functions (CREATE FUNCTION) within the
                    database.
                  </li>
                </ul>
              </ul>
              <Typography.Paragraph>
                <strong>Step 2a:</strong> Create the p0GrantOrRevokeUserAccess
                function:
              </Typography.Paragraph>
            </>
          ),
          command: sql.grantOrRevokeAccess,
        },
        {
          header: (
            <Typography.Paragraph>
              <strong>Step 2b:</strong> Create the p0ManageRoleWithGrants
              function:
            </Typography.Paragraph>
          ),
          command: sql.manageRoleWithGrants,
        },
        {
          header: (
            <Typography.Paragraph>
              <strong>Step 3:</strong> Grant the P0 service account execute
              access to the newly created function:
            </Typography.Paragraph>
          ),
          command: sql.grantPermissions,
        },
      ],
    },
  };
};
